<template>
	<div data-tour-scope="push-notifications">
		<v-main>
			<v-container fluid class="pn-page">
				<div class="pn-top">
					<div>
						<div class="pn-title">Notifiche push</div>
						<div class="pn-muted">Arrivano sul telefono o sul browser dei clienti che hanno accettato le notifiche (Web, iOS, Android).</div>
					</div>
					<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
					<v-spacer></v-spacer>
					<v-btn outlined color="primary" data-tour="history" @click="showHistoryDialog = true">
						<v-icon left>mdi-history</v-icon>Cronologia invii
						<v-chip v-if="notificationHistory.length" x-small class="ml-2">{{ notificationHistory.length }}</v-chip>
					</v-btn>
				</div>

				<v-form ref="notificationForm" v-model="formValid">
					<v-row>
						<v-col cols="12" md="7" lg="8">
							<!-- contenuto -->
							<SectionCard title="Contenuto" icon="mdi-message-text-outline" subtitle="cosa legge il cliente" data-tour="content">
								<v-text-field
									v-model="notification.title"
									label="Titolo *"
									placeholder="Es. Saldi di fine stagione"
									counter="65"
									:rules="[rules.required, rules.maxLength(65)]"
									prepend-inner-icon="mdi-format-title"
									outlined
									dense
									class="mb-2"
								/>
								<v-textarea
									v-model="notification.body"
									label="Messaggio *"
									placeholder="Es. Fino al 50% su una selezione di prodotti, solo fino a domenica."
									counter="240"
									:rules="[rules.required, rules.maxLength(240)]"
									prepend-inner-icon="mdi-text"
									outlined
									rows="3"
									auto-grow
									class="mb-2"
								/>
								<v-row class="pn-fields">
									<v-col cols="12" md="6" data-tour="media">
										<v-text-field
											v-model="notification.image"
											label="Immagine (facoltativa)"
											placeholder="https://…/immagine.jpg"
											hint="URL dell'immagine mostrata nella notifica"
											persistent-hint
											prepend-inner-icon="mdi-image-outline"
											outlined
											dense
											clearable
										/>
									</v-col>
									<v-col cols="12" md="6">
										<v-text-field
											v-model="notification.link"
											label="Link (facoltativo)"
											placeholder="https://…"
											hint="Dove porta il cliente quando tocca la notifica"
											persistent-hint
											prepend-inner-icon="mdi-link-variant"
											outlined
											dense
											clearable
										/>
									</v-col>
								</v-row>
							</SectionCard>

							<!-- destinatari -->
							<SectionCard title="Destinatari" icon="mdi-account-group-outline" :subtitle="riepilogoDestinatari" class="mt-4" data-tour="target">
								<div class="pn-choices pn-choices--2">
									<button
										v-for="t in destinatari"
										:key="t.value"
										type="button"
										class="pn-choice"
										:class="{ 'pn-choice--on': notification.targetType === t.value }"
										:aria-pressed="notification.targetType === t.value ? 'true' : 'false'"
										@click="scegliDestinatari(t.value)"
									>
										<v-icon :color="notification.targetType === t.value ? 'primary' : 'grey'" class="mr-3">{{ t.icon }}</v-icon>
										<span>
											<span class="pn-choice-label">{{ t.label }}</span>
											<span class="pn-muted d-block">{{ t.description }}</span>
										</span>
									</button>
								</div>
								<template v-if="notification.targetType === 'segment'">
									<div class="pn-section mt-4">Quale segmento</div>
									<div class="pn-choices">
										<button
											v-for="s in segments"
											:key="s.value"
											type="button"
											class="pn-choice"
											:class="{ 'pn-choice--on': notification.segment === s.value }"
											:aria-pressed="notification.segment === s.value ? 'true' : 'false'"
											@click="notification.segment = s.value"
										>
											<v-icon :color="notification.segment === s.value ? 'primary' : 'grey'" class="mr-3">{{ s.icon }}</v-icon>
											<span>
												<span class="pn-choice-label">{{ s.label }}</span>
												<span class="pn-muted d-block">{{ s.description }}</span>
											</span>
										</button>
									</div>
								</template>
							</SectionCard>

							<!-- piattaforme -->
							<SectionCard title="Piattaforme" icon="mdi-cellphone-link" :subtitle="notification.platforms.length + ' su ' + availablePlatforms.length" class="mt-4" data-tour="platforms">
								<div class="pn-choices pn-choices--3">
									<button
										v-for="p in availablePlatforms"
										:key="p.value"
										type="button"
										class="pn-choice pn-choice--center"
										:class="{ 'pn-choice--on': notification.platforms.includes(p.value) }"
										:aria-pressed="notification.platforms.includes(p.value) ? 'true' : 'false'"
										@click="cambiaPiattaforma(p.value)"
									>
										<v-icon :color="notification.platforms.includes(p.value) ? 'primary' : 'grey'" class="mr-2">{{ p.icon }}</v-icon>
										<span class="pn-choice-label">{{ p.label }}</span>
										<v-icon small class="ml-auto" :color="notification.platforms.includes(p.value) ? 'primary' : 'grey lighten-1'">{{
											notification.platforms.includes(p.value) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
										}}</v-icon>
									</button>
								</div>
								<div v-if="!notification.platforms.length" class="pn-error mt-2">Scegli almeno una piattaforma.</div>
							</SectionCard>

							<!-- quando -->
							<SectionCard title="Quando" icon="mdi-clock-outline" :subtitle="notification.scheduleType === 'scheduled' ? 'programmata' : 'subito'" class="mt-4" data-tour="schedule">
								<v-btn-toggle v-model="notification.scheduleType" mandatory dense color="primary" class="pn-toggle">
									<v-btn value="now"><v-icon left small>mdi-send-outline</v-icon>Invia subito</v-btn>
									<v-btn value="scheduled"><v-icon left small>mdi-calendar-clock</v-icon>Programma</v-btn>
								</v-btn-toggle>
								<v-row v-if="notification.scheduleType === 'scheduled'" class="pn-fields mt-2">
									<v-col cols="6" md="4">
										<v-text-field v-model="notification.scheduledDate" label="Data" type="date" :min="oggi" outlined dense :rules="[rules.required]" />
									</v-col>
									<v-col cols="6" md="4">
										<v-text-field v-model="notification.scheduledTime" label="Ora" type="time" outlined dense :rules="[rules.required]" />
									</v-col>
								</v-row>
							</SectionCard>
						</v-col>

						<!-- anteprima e riepilogo -->
						<v-col cols="12" md="5" lg="4">
							<div class="pn-side">
								<SectionCard title="Anteprima" icon="mdi-eye-outline" data-tour="preview">
									<PushPreview :titolo="notification.title" :messaggio="notification.body" :immagine="notification.image || ''" :link="notification.link || ''" />
								</SectionCard>
								<SectionCard title="Riepilogo" icon="mdi-clipboard-check-outline" class="mt-4">
									<div class="pn-sum"><v-icon small class="mr-2">mdi-account-group-outline</v-icon>{{ riepilogoDestinatari }}</div>
									<div class="pn-sum"><v-icon small class="mr-2">mdi-cellphone-link</v-icon>{{ riepilogoPiattaforme }}</div>
									<div class="pn-sum"><v-icon small class="mr-2">mdi-clock-outline</v-icon>{{ riepilogoQuando }}</div>
									<div v-if="mancano.length" class="pn-missing mt-3">
										<div class="font-weight-medium mb-1"><v-icon x-small color="warning" class="mr-1">mdi-alert-outline</v-icon>Prima di inviare manca:</div>
										<div v-for="m in mancano" :key="m">· {{ m }}</div>
									</div>
									<div v-else class="pn-ready mt-3"><v-icon x-small color="success" class="mr-1">mdi-check-circle-outline</v-icon>Tutto pronto per l'invio</div>
								</SectionCard>
							</div>
						</v-col>
					</v-row>
				</v-form>

				<!-- azioni sempre in vista -->
				<div class="pn-actions">
					<span class="pn-muted">{{
						mancano.length ? 'Completa i campi mancanti per inviare' : notification.scheduleType === 'scheduled' ? 'La notifica partirà alla data scelta' : 'La notifica partirà appena premi Invia'
					}}</span>
					<v-spacer></v-spacer>
					<v-btn text class="mr-2" data-tour="reset" @click="resetForm"><v-icon left>mdi-refresh</v-icon>Resetta</v-btn>
					<v-btn color="primary" depressed data-tour="send" :disabled="!formValid || mancano.length > 0" :loading="sending" @click="sendNotification">
						<v-icon left>mdi-send</v-icon>{{ notification.scheduleType === 'scheduled' ? 'Programma invio' : 'Invia notifica' }}
					</v-btn>
				</div>

				<!-- cronologia -->
				<v-dialog v-model="showHistoryDialog" max-width="1200px">
					<v-card>
						<v-card-title class="pn-dialog-title">
							<v-icon color="primary" class="mr-2">mdi-history</v-icon>Cronologia invii
							<v-spacer />
							<v-btn icon small title="Chiudi" @click="showHistoryDialog = false"><v-icon>mdi-close</v-icon></v-btn>
						</v-card-title>
						<v-card-text class="pa-0">
							<v-data-table
								:headers="historyHeaders"
								:items="notificationHistory"
								:items-per-page="10"
								:loading="loadingHistory"
								class="elevation-0"
								:hide-default-header="mobileCards"
								:class="{ 'table-cards': mobileCards }"
								no-data-text="Nessuna notifica inviata finora"
							>
								<!-- telefono: ogni invio diventa una card chiave/valore (su desktop lo slot non esiste e restano le colonne) -->
								<template v-if="mobileCards" #body="{ items }">
									<tbody class="push-cards">
										<tr v-for="(item, index) in items" :key="item._id || index">
											<td :colspan="historyHeaders.length" class="px-3 py-1">
												<key-value-card :title="item.title || 'Notifica'" :fields="campiCard(item)">
													<template #actions>
														<v-chip small label outlined class="mr-2" :color="getStatusColor(item.status)">{{ getStatusLabel(item.status) }}</v-chip>
													</template>
													<div v-if="(item.platforms || []).length">
														<v-chip v-for="platform in item.platforms" :key="platform" x-small class="mr-1">{{ nomePiattaforma(platform) }}</v-chip>
													</div>
												</key-value-card>
											</td>
										</tr>
										<tr v-if="!items.length">
											<td :colspan="historyHeaders.length" class="text-center grey--text py-6">Nessuna notifica inviata finora</td>
										</tr>
									</tbody>
								</template>
								<template #item.createdAt="{ item }">
									<span class="text-no-wrap">{{ formatDate(item.createdAt) }}</span>
								</template>
								<template #item.title="{ item }">
									<div class="font-weight-medium">{{ item.title }}</div>
									<div v-if="item.body" class="pn-muted pn-clamp">{{ item.body }}</div>
								</template>
								<template #item.targetType="{ item }">
									{{ nomeDestinatari(item) }}
								</template>
								<template #item.status="{ item }">
									<v-chip small label outlined :color="getStatusColor(item.status)">{{ getStatusLabel(item.status) }}</v-chip>
								</template>
								<template #item.platforms="{ item }">
									<v-icon v-for="platform in item.platforms" :key="platform" small class="mr-1" :title="nomePiattaforma(platform)">{{ iconaPiattaforma(platform) }}</v-icon>
								</template>
								<template #item.sentCount="{ item }">
									<div class="text-center"><v-icon small left color="success">mdi-check-circle</v-icon>{{ item.sentCount || 0 }}</div>
								</template>
								<template #item.failedCount="{ item }">
									<div v-if="item.failedCount > 0" class="text-center"><v-icon small left color="error">mdi-alert-circle</v-icon>{{ item.failedCount }}</div>
									<div v-else class="text-center grey--text">-</div>
								</template>
							</v-data-table>
						</v-card-text>
					</v-card>
				</v-dialog>

				<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :multi-line="snackbar.mode === 'multi-line'" :timeout="snackbar.timeout" :vertical="snackbar.mode === 'vertical'">
					{{ snackbar.text }}
					<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
				</v-snackbar>
			</v-container>
		</v-main>
		<TourOverlay :tour="tour" />
	</div>
</template>

<script>
import axios from 'axios';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createPushNotificationsTourSteps } from './push-notifications-tour';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import SectionCard from '@/components/core/SectionCard.vue';
import PushPreview from '@/components/ecommerce/push/PushPreview.vue';

export default {
	name: 'PushNotifications',

	components: {
		TourOverlay,
		KeyValueCard,
		SectionCard,
		PushPreview,
	},

	setup() {
		return { tour: useTour(createPushNotificationsTourSteps()) };
	},

	data() {
		return {
			isMobile: false,
			formValid: false,
			sending: false,
			showHistoryDialog: false,
			loadingHistory: false,

			notification: {
				title: '',
				body: '',
				image: '',
				link: '',
				targetType: 'all',
				segment: null,
				platforms: ['web', 'ios', 'android'],
				scheduleType: 'now',
				scheduledDate: null,
				scheduledTime: null,
			},

			destinatari: [
				{ value: 'all', label: 'Tutti gli utenti registrati', description: 'Chi ha abilitato le notifiche push', icon: 'mdi-account-multiple-outline' },
				{ value: 'segment', label: 'Un segmento', description: 'Solo un gruppo di clienti', icon: 'mdi-account-filter-outline' },
			],
			segments: [
				{
					value: 'recent_customers',
					label: 'Clienti recenti',
					description: 'Clienti che hanno effettuato un ordine negli ultimi 30 giorni',
					icon: 'mdi-account-clock',
				},
				{
					value: 'vip_customers',
					label: 'Clienti VIP',
					description: 'Clienti con più di 5 ordini completati',
					icon: 'mdi-star-circle',
				},
				{
					value: 'inactive_customers',
					label: 'Clienti inattivi',
					description: 'Clienti senza ordini negli ultimi 90 giorni',
					icon: 'mdi-account-off',
				},
				{
					value: 'newsletter_subscribers',
					label: 'Iscritti newsletter',
					description: 'Utenti iscritti alla newsletter',
					icon: 'mdi-email-newsletter',
				},
			],

			availablePlatforms: [
				{
					value: 'web',
					label: 'Web',
					icon: 'mdi-web',
					color: 'blue',
				},
				{
					value: 'ios',
					label: 'iOS',
					icon: 'mdi-apple',
					color: 'grey darken-2',
				},
				{
					value: 'android',
					label: 'Android',
					icon: 'mdi-android',
					color: 'green',
				},
			],

			rules: {
				required: v => !!v || 'Campo obbligatorio',
				maxLength: max => v => (v && v.length <= max) || `Massimo ${max} caratteri`,
				minArray: min => v => (v && v.length >= min) || `Seleziona almeno ${min} elemento`,
			},

			snackbar: {
				snackbar: false,
				color: '',
				mode: '',
				timeout: 6000,
				text: '',
			},

			historyHeaders: [
				{ text: 'Data', value: 'createdAt', width: '150px' },
				{ text: 'Titolo', value: 'title' },
				{ text: 'Destinatari', value: 'targetType', width: '120px' },
				{ text: 'Piattaforme', value: 'platforms', width: '150px' },
				{ text: 'Inviati', value: 'sentCount', width: '100px', align: 'center' },
				{ text: 'Falliti', value: 'failedCount', width: '100px', align: 'center' },
				{ text: 'Stato', value: 'status', width: '120px' },
			],

			notificationHistory: [],
		};
	},

	computed: {
		oggi() {
			return new Date().toISOString().slice(0, 10);
		},
		// cio' che manca per poter inviare (stesse regole del form, piu' piattaforme/segmento/data)
		mancano() {
			const n = this.notification;
			const m = [];
			if (!String(n.title || '').trim()) m.push('il titolo');
			if (!String(n.body || '').trim()) m.push('il messaggio');
			if (n.targetType === 'segment' && !n.segment) m.push('il segmento');
			if (!n.platforms.length) m.push('almeno una piattaforma');
			if (n.scheduleType === 'scheduled') {
				if (!n.scheduledDate || !n.scheduledTime) m.push("data e ora dell'invio");
				else if (new Date(`${n.scheduledDate}T${n.scheduledTime}:00`) <= new Date()) m.push("una data e un'ora nel futuro");
			}
			return m;
		},
		riepilogoDestinatari() {
			const n = this.notification;
			if (n.targetType !== 'segment') return 'Tutti gli utenti registrati';
			const s = this.segments.find(x => x.value === n.segment);
			return s ? `Segmento: ${s.label}` : 'Segmento da scegliere';
		},
		riepilogoPiattaforme() {
			const nomi = this.availablePlatforms.filter(p => this.notification.platforms.includes(p.value)).map(p => p.label);
			return nomi.length ? nomi.join(', ') : 'Nessuna piattaforma';
		},
		riepilogoQuando() {
			const n = this.notification;
			if (n.scheduleType !== 'scheduled') return 'Subito, appena premi Invia';
			if (!n.scheduledDate || !n.scheduledTime) return 'Programmata: data e ora da scegliere';
			return `Programmata per il ${this.formatDate(`${n.scheduledDate}T${n.scheduledTime}:00`)}`;
		},
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		...mapGetters({
			getServices: 'services/getServices',
			getAutenticate: 'profile/getAutenticate',
			getToken: 'profile/getToken',
			getUrl: 'settings/getUrl',
		}),
	},

	async mounted() {
		this.setPreload(true);
		let promises = [];
		promises.push(this.fetchSettings());
		promises.push(this.fetchService(['firebase_push']));
		await Promise.all(promises);
		// Load Firebase Push configuration if exists
		let firebasePush = _.find(this.getServices, set => {
			return set.service === 'firebase_push';
		});

		if (firebasePush) {
			this.availablePlatforms = this.availablePlatforms.filter(platform => firebasePush.setting.channels.includes(platform.value));
		}

		await this.loadHistory();
		this.setPreload(false);
	},

	methods: {
		...mapActions({
			fetchSettings: 'settings/asyncFetchData',
			fetchService: 'services/getServiceFetch',
		}),
		...mapMutations({
			setPreload: 'settings/setPreload',
			setLogout: 'profile/setLogout',
		}),

		// coppie chiave/valore della card da telefono
		campiCard(item) {
			return [
				{ label: 'Data', value: this.formatDate(item.createdAt) },
				{ label: 'Destinatari', value: item.targetType },
				{ label: 'Inviati', value: item.sentCount || 0, strong: true },
				{ label: 'Falliti', value: item.failedCount > 0 ? item.failedCount : '', strong: item.failedCount > 0 },
			];
		},

		onResize() {
			this.isMobile = window.innerWidth < 769;
		},

		scegliDestinatari(valore) {
			this.notification.targetType = valore;
			if (valore !== 'segment') this.notification.segment = null;
		},
		cambiaPiattaforma(valore) {
			const p = this.notification.platforms;
			this.notification.platforms = p.includes(valore) ? p.filter(x => x !== valore) : [...p, valore];
		},
		nomePiattaforma(valore) {
			const p = this.availablePlatforms.find(x => x.value === valore) || { web: { label: 'Web' }, ios: { label: 'iOS' }, android: { label: 'Android' } }[valore];
			return (p && p.label) || valore;
		},
		iconaPiattaforma(valore) {
			return { web: 'mdi-web', ios: 'mdi-apple', android: 'mdi-android' }[valore] || 'mdi-cellphone';
		},
		nomeDestinatari(item) {
			if (item.targetType !== 'segment') return 'Tutti';
			const s = this.segments.find(x => x.value === item.segment);
			return s ? s.label : 'Segmento';
		},
		resetForm() {
			this.notification = {
				title: '',
				body: '',
				image: '',
				link: '',
				targetType: 'all',
				segment: null,
				platforms: ['web', 'ios', 'android'],
				scheduleType: 'now',
				scheduledDate: null,
				scheduledTime: null,
			};
			this.$refs.notificationForm.resetValidation();
		},

		async sendNotification() {
			if (!this.$refs.notificationForm.validate() || this.mancano.length) {
				return;
			}

			this.sending = true;

			try {
				const payload = {
					title: this.notification.title,
					body: this.notification.body,
					image: this.notification.image || null,
					link: this.notification.link || null,
					targetType: this.notification.targetType,
					segment: this.notification.segment,
					platforms: this.notification.platforms,
					scheduleType: this.notification.scheduleType,
					scheduledAt: null,
				};

				if (this.notification.scheduleType === 'scheduled') {
					payload.scheduledAt = `${this.notification.scheduledDate}T${this.notification.scheduledTime}:00`;
				}

				const response = await axios.post(`${this.getUrl}notifications/push/send`, payload, {
					headers: { Authorization: `Bearer ${this.getToken}` },
				});

				this.snackbar.color = 'success';
				this.snackbar.text = this.notification.scheduleType === 'scheduled' ? 'Notifica programmata con successo!' : `Notifica inviata con successo a ${response.data.sentCount} dispositivi!`;
				this.snackbar.snackbar = true;
				this.snackbar.mode = 'multi-line';
				this.snackbar.timeout = 5000;

				this.resetForm();
				this.loadHistory();
			} catch (err) {
				console.error(err);
				this.snackbar.color = 'error';
				this.snackbar.text = err.response?.data?.error || "Errore durante l'invio della notifica";
				this.snackbar.snackbar = true;
				this.snackbar.mode = 'multi-line';
				this.snackbar.timeout = 5000;

				if (err.response?.status === 401) {
					this.setLogout();
					this.$router.push('/login');
				}
			} finally {
				this.sending = false;
			}
		},

		async loadHistory() {
			this.loadingHistory = true;
			try {
				const response = await axios.get(`${this.getUrl}notifications/push/history`, {
					headers: { Authorization: `Bearer ${this.getToken}` },
				});
				this.notificationHistory = response.data.data || [];
			} catch (err) {
				console.error(err);
				this.snackbar.color = 'error';
				this.snackbar.text = 'Errore durante il caricamento della cronologia';
				this.snackbar.snackbar = true;
				this.snackbar.mode = 'multi-line';
				this.snackbar.timeout = 5000;
			} finally {
				this.loadingHistory = false;
			}
		},

		formatDate(dateString) {
			if (!dateString) return '-';
			const date = new Date(dateString);
			return date.toLocaleString('it-IT', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		},

		getStatusColor(status) {
			const colors = {
				sent: 'success',
				scheduled: 'warning',
				failed: 'error',
				sending: 'info',
			};
			return colors[status] || 'grey';
		},

		getStatusLabel(status) {
			const labels = {
				sent: 'Inviata',
				scheduled: 'Programmata',
				failed: 'Fallita',
				sending: 'Invio in corso',
			};
			return labels[status] || status;
		},
	},

	beforeDestroy() {
		this.tour.stop();
	},
};
</script>

<style scoped>
.pn-page {
	max-width: 1600px;
	padding-bottom: 0;
}
.pn-top {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px 12px;
	margin-bottom: 16px;
}
.pn-title {
	font-size: 20px;
	font-weight: 600;
	color: #1f2d3d;
}
.pn-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.pn-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
}
.pn-fields {
	margin: 0 -6px;
}
.pn-fields > [class*='col'] {
	padding: 6px;
}
.pn-page ::v-deep .section-card {
	height: auto;
}
.pn-side {
	position: sticky;
	top: 72px;
}
/* scelte a card (destinatari, segmenti, piattaforme) */
.pn-choices {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: 8px;
}
.pn-choices--2 {
	grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}
.pn-choices--3 {
	grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
}
.pn-choice {
	display: flex;
	align-items: center;
	padding: 10px 12px;
	border: 1px solid #e1e7ef;
	border-radius: 10px;
	background: #fff;
	text-align: left;
	cursor: pointer;
	transition: border-color 0.15s, background 0.15s;
}
.pn-choice:hover {
	border-color: #9fb6d3;
}
.pn-choice--on {
	border-color: #01579b;
	background: #eef4fd;
}
.pn-choice:focus-visible {
	outline: 2px solid #01579b;
	outline-offset: 1px;
}
.pn-choice-label {
	font-size: 14px;
	font-weight: 600;
	color: #1f2d3d;
}
.pn-toggle .v-btn {
	text-transform: none;
	letter-spacing: 0;
}
.pn-error {
	font-size: 12px;
	color: #b3261e;
}
.pn-sum {
	display: flex;
	align-items: flex-start;
	padding: 4px 0;
	font-size: 13px;
	color: #1f2d3d;
}
.pn-missing {
	padding: 8px 10px;
	border-radius: 8px;
	background: #fff6e5;
	color: #7a4a00;
	font-size: 12px;
}
.pn-ready {
	padding: 8px 10px;
	border-radius: 8px;
	background: #eaf6ec;
	color: #1e5b2b;
	font-size: 12px;
}
/* azioni: sempre in fondo alla vista */
.pn-actions {
	position: sticky;
	bottom: 0;
	z-index: 4;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	margin: 16px -12px 0;
	padding: 10px 16px;
	background: #fff;
	border-top: 1px solid #e1e7ef;
	box-shadow: 0 -4px 12px rgba(31, 45, 61, 0.06);
}
.pn-dialog-title {
	font-size: 17px;
	font-weight: 600;
}
.pn-clamp {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	max-width: 420px;
}
@media (max-width: 959px) {
	.pn-side {
		position: static;
	}
}
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.push-cards,
.push-cards tr {
	display: block;
	width: 100%;
}
.push-cards tr,
.push-cards tr:hover {
	background: transparent !important;
}
.push-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
/* respiro tra le card e il bordo della card che contiene la tabella */
.push-cards tr:first-child td {
	padding-top: 12px !important;
}
.push-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.push-cards tr + tr td {
	padding-top: 8px !important;
}
</style>
